import { type FC, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckBox, DropButton, Grid, ResponsiveContext } from 'grommet';
import { Configure } from 'grommet-icons/icons';
import { DropMenuButton } from '../buttons/DropMenuButton';
import { getDefaultColumns } from './ColumnsHelper';
import { Box, Card, CardBody, CardHeader } from '/src/components';
import { tooltipMessages } from '/src/lib/tooltips';
import { pxToRem } from '/src/utils/formatting';

export const ListSettingsButton: FC<ListSettingsButtonProps> = (props) => {
  const { shownColumns, availableColumns, disabled = false, onChange } = props;
  const tooltips = tooltipMessages();
  const { t } = useTranslation();

  const columnsList = useMemo<{ label: string; value: string }[]>(
    () => (availableColumns ? availableColumns.map((name) => ({ label: name, value: name })) : []),
    [t, availableColumns]
  );

  const screenSize = useContext(ResponsiveContext);

  const isMobile = useMemo(() => screenSize === 'small' || screenSize === 'xsmall', [screenSize]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuHovered, setIsMenuHovered] = useState(false);

  const handleToggle = (value: string, isChecked: boolean) => {
    let newValues: string[];
    if (isChecked) {
      newValues = [...shownColumns, value];
    } else {
      newValues = shownColumns.filter((id) => id !== value);
    }

    onChange?.(newValues);
  };

  const handleSelectAll = () => {
    onChange?.([...(availableColumns || [])]);
  };

  const handleClear = () => {
    onChange?.([]);
  };

  const handleSetDefault = () => {
    onChange?.(getDefaultColumns(availableColumns));
  };

  return (
    <DropButton
      plain
      disabled={disabled}
      open={isMenuOpen}
      onOpen={() => setIsMenuOpen(true)}
      onClose={() => setIsMenuOpen(false)}
      onMouseOver={() => setIsMenuHovered(true)}
      onMouseOut={() => setIsMenuHovered(false)}
      tip={tooltips.columnSettings}
      dropAlign={{ right: 'right', top: 'bottom' }}
      dropContent={
        <Card width={{ min: pxToRem(600) }}>
          <CardHeader
            pad={{ horizontal: 'medium', vertical: 'small' }}
            title={t('apiData:selectColumns')}
            icon={<Configure size="24px" color="brand" />}
          >
            <Box direction="row" gap="xsmall">
              <DropMenuButton label="Select All" onClick={handleSelectAll} />
              <DropMenuButton label="Default" onClick={handleSetDefault} />
              <DropMenuButton label="Clear All" onClick={handleClear} />
            </Box>
          </CardHeader>
          <CardBody pad="1.5rem" gap="medium">
            <Grid rows="auto" columns={{ count: 3, size: 'flex' }} gap="small">
              {columnsList?.map((item) => (
                <CheckBox
                  label={item.label}
                  key={item.value}
                  checked={shownColumns.includes(item.value)}
                  onChange={(event) => handleToggle(item.value, event.target.checked)}
                />
              ))}
            </Grid>
          </CardBody>
        </Card>
      }
    >
      <Box
        direction="row"
        align="center"
        height={isMobile ? undefined : 'xxlarge'}
        style={{ boxShadow: isMenuHovered ? 'rgba(0, 0, 0, 0.2) 0 10px 10px -10px' : '' }}
        transitionProperty="background, color, box-shadow, padding-bottom"
        transitionDuration="0.15s, 0.15s, 0.15s, 0.15s"
        transitionTimingFunction="ease, ease, ease, ease"
        pad={{
          horizontal: '0.75rem',
          vertical: isMobile ? '0.5rem' : undefined,
          bottom: isMenuHovered ? '1px' : undefined,
        }}
        gap="xsmall"
        border={{ color: 'light-5', size: '0.8px' }}
        borderRadius="6px"
        cursor="pointer"
        round="6px"
      >
        <Configure color={isMenuOpen ? 'brand' : undefined} size="14px" />
      </Box>
    </DropButton>
  );
};

export type ListSettingsButtonProps = {
  shownColumns: string[];
  availableColumns: string[];
  disabled?: boolean;
  onChange: (newValues: string[]) => void;
};
